// Diagnostics — answers "why does the portal blank / refresh periodically"
// with DATA instead of theory. Every key lifecycle event is timestamped
// to the browser console, the in-app DebugConsole, and log-api
// (session "CloudV2-diag") so we can review server-side after the fact.
//
// The single most decisive signal is recordMount(): a full page reload
// resets all JS, so it fires a fresh mount with navigation type 'reload'.
// If the "blank screen" is a hard reload, we'll see mount#N with
// nav=reload every N seconds. If it's just a re-render (no reload), there
// is NO new mount — which immediately rules a whole class of causes out.

import { logToApi } from './logApi';

const DIAG_SESSION = 'CloudV2-diag';
const MOUNT_KEY = 'cloud-diag-mounts';

// Fire to console + DebugConsole (via console.info, which DebugConsole
// captures) + log-api. log-api is fire-and-forget; we don't await.
export function diag(message: string, extra?: Record<string, unknown>) {
  // Prepend a wall-clock timestamp — log-api's own timestamp field comes
  // back empty, so without this we can't sequence events by real time or
  // correlate a reload with a deploy / SSE drop / backend log. HH:MM:SS
  // is enough granularity for "what happened right before the blank".
  const t = new Date().toLocaleTimeString('de-DE', { hour12: false });
  const stamped = `${t} ${message}`;
  const line = extra ? `${stamped} ${JSON.stringify(extra)}` : stamped;
  // console.info so it lands in DebugConsole's capture + the real console.
  console.info(`[diag] ${line}`);
  void logToApi(DIAG_SESSION, 'Log', stamped, extra);
}

interface MountRecord { count: number; lastTs: number; }

function readMounts(): MountRecord {
  try {
    const raw = localStorage.getItem(MOUNT_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { count: 0, lastTs: 0 };
}

function navType(): string {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    return nav?.type ?? 'unknown';
  } catch { return 'unknown'; }
}

// Call once at the very top of app startup. Logs mount#, seconds since
// the previous mount (the reload cadence!), and the navigation type.
export function recordMount() {
  const prev = readMounts();
  const now = Date.now();
  const sinceLast = prev.lastTs ? Math.round((now - prev.lastTs) / 1000) : null;
  const rec: MountRecord = { count: prev.count + 1, lastTs: now };
  try { localStorage.setItem(MOUNT_KEY, JSON.stringify(rec)); } catch { /* ignore */ }

  const nav = navType();
  // Was the page controlled by a service worker that just took over?
  let swControlled = false;
  try { swControlled = !!navigator.serviceWorker?.controller; } catch { /* ignore */ }

  diag(`app mount #${rec.count}`, {
    nav,                                // 'reload' | 'navigate' | 'back_forward'
    sinceLastMountSec: sinceLast,       // gap to previous mount = reload cadence
    swControlled,
    visibility: typeof document !== 'undefined' ? document.visibilityState : 'unknown',
    url: typeof location !== 'undefined' ? location.pathname : '',
  });

  // Service-worker takeover is a classic silent-reload trigger — log it.
  try {
    navigator.serviceWorker?.addEventListener?.('controllerchange', () => {
      diag('SW controllerchange (new service worker took control — reload likely)');
    });
    // Detect a NEW SW being found/installed — the precursor to a
    // potential reload. If reloads correlate with these, the SW is the
    // culprit (likely an old autoUpdate SW still resident on the device).
    navigator.serviceWorker?.getRegistration?.().then((reg) => {
      if (!reg) { diag('SW: no registration'); return; }
      diag('SW: registration present', {
        active: !!reg.active, waiting: !!reg.waiting, installing: !!reg.installing,
      });
      reg.addEventListener('updatefound', () => {
        diag('SW updatefound (new version installing — reload may follow if autoUpdate SW)');
      });
    }).catch(() => {});
  } catch { /* ignore */ }

  // Page-hide / freeze are reload precursors on mobile (bfcache evict).
  window.addEventListener('pagehide', (e) => {
    diag('pagehide', { persisted: (e as PageTransitionEvent).persisted });
  });
  document.addEventListener('visibilitychange', () => {
    diag('visibilitychange', { state: document.visibilityState });
  });
}
